import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { questions as allQuestionsData } from '../data/questions';
import {
  Question,
  Tier,
  GameMode,
  Category,
  MultiplierBreakdown,
  TIMER_BY_DIFFICULTY,
  STANDARD_QUESTION_COUNT,
} from '../types/game';
import { useSeenQuestions } from '../hooks/useSeenQuestions';
import { checkAnswer, fiftyFifty, submitScore } from '../api/client';
import { calculateMultipliers } from '../hooks/useScoring';
import { usePowerUps } from '../hooks/usePowerUps';
import { useAchievements } from '../hooks/useAchievements';
import GameHUD from './GameHUD';
import MultiplierPopup from './MultiplierPopup';
import GameResult from './GameResult';
import PowerUpBar from './PowerUpBar';
import AchievementToast from './AchievementToast';

interface TriviaGameProps {
  userTier: Tier;
  onUpgrade: (tier: 'PREMIUM' | 'VIP') => void;
  gameMode?: GameMode;
  dailyQuestionIds?: number[];
  // Authenticated wallet (SIWE). When set, the final result is recorded to the
  // leaderboard; otherwise the player simply isn't ranked.
  authedAddress?: string | null;
}

const TriviaGame: React.FC<TriviaGameProps> = ({
  userTier,
  onUpgrade,
  gameMode = 'STANDARD',
  dailyQuestionIds,
  authedAddress,
}) => {
  // Core game state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [hearts, setHearts] = useState(3);
  const [correctStreak, setCorrectStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [showChallengeBreak, setShowChallengeBreak] = useState(false);
  const [challengeBreakShown, setChallengeBreakShown] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  // Synchronous mirror of correctAnswers. On the final question, finishGame runs
  // in the same handler as setCorrectAnswers, before that state has flushed — so
  // the ref gives finishGame the true count for the leaderboard submission.
  const correctAnswersRef = useRef(0);

  // Timer state — dynamic per difficulty
  const [timeLeft, setTimeLeft] = useState(8);
  const timerFrozenRef = useRef(false);

  // Scoring state
  const answerStartTimeRef = useRef(Date.now());
  const [lastBreakdown, setLastBreakdown] = useState<MultiplierBreakdown | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [scoreAnimating, setScoreAnimating] = useState(false);
  const popupKeyRef = useRef(0);

  // Power-ups
  const { remaining: powerUps, activateFiftyFifty, activateTimeFreeze, activateSkip, reset: resetPowerUps } = usePowerUps(userTier);
  const [hiddenOptions, setHiddenOptions] = useState<Set<number>>(new Set());

  // Surfaced when a server-side validation request fails (e.g. backend down)
  const [answerError, setAnswerError] = useState<string | null>(null);

  // Seen-questions dedup
  const { markSeen, getUnseen } = useSeenQuestions();

  // Achievements
  const {
    toastQueue,
    dismissToast,
    checkAfterAnswer,
    checkAfterGame,
    sessionUnlocked,
  } = useAchievements();

  // Answer time tracking for average
  const answerTimesRef = useRef<number[]>([]);
  const hardCorrectRef = useRef(0);
  const hardTotalRef = useRef(0);

  // Fisher-Yates shuffle
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Build question set based on mode
  const availableQuestions = useMemo(() => {
    if (gameMode === 'DAILY_CHALLENGE' && dailyQuestionIds) {
      // Daily: use exact question IDs in order
      return dailyQuestionIds
        .map((id) => allQuestionsData.find((q) => q.id === id))
        .filter(Boolean) as Question[];
    }

    // Standard: filter by tier
    const filtered = allQuestionsData.filter((q) => {
      if (userTier === 'FREE') return q.tier === 'FREE';
      if (userTier === 'PREMIUM') return q.tier === 'FREE' || q.tier === 'PREMIUM';
      return true;
    });

    // Exclude recently-seen questions
    const unseen = getUnseen(filtered, STANDARD_QUESTION_COUNT);

    // Group by category for round-robin balancing
    const byCategory = new Map<Category, Question[]>();
    for (const q of shuffleArray(unseen)) {
      const list = byCategory.get(q.category) || [];
      list.push(q);
      byCategory.set(q.category, list);
    }

    // Round-robin pick across categories
    const picked: Question[] = [];
    const cats = Array.from(byCategory.keys());
    let idx = 0;
    while (picked.length < STANDARD_QUESTION_COUNT && cats.length > 0) {
      const cat = cats[idx % cats.length];
      const list = byCategory.get(cat)!;
      if (list.length > 0) {
        picked.push(list.shift()!);
      } else {
        cats.splice(idx % cats.length, 1);
        if (cats.length === 0) break;
        continue;
      }
      idx++;
    }

    // Sort by difficulty: EASY → MEDIUM → HARD
    const diffOrder = { EASY: 0, MEDIUM: 1, HARD: 2 };
    picked.sort((a, b) => diffOrder[a.difficulty] - diffOrder[b.difficulty]);

    return picked;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTier, gameMode]);

  const currentQ = availableQuestions[currentQuestion];
  const maxTime = currentQ ? TIMER_BY_DIFFICULTY[currentQ.difficulty] : 8;

  // Shuffle the on-screen positions of the options per question, so the correct
  // answer doesn't sit in a fixed slot that a repeat player could memorize.
  // This is display-only: every piece of answer logic (server check, selection,
  // 50/50 hiding) keys off the ORIGINAL option index, so correctness stays
  // server-authoritative and unchanged. Memoized on the question id so the
  // options stay put while the player reads — they only reshuffle per question.
  const displayOrder = useMemo<number[]>(() => {
    if (!currentQ) return [];
    return shuffleArray(currentQ.options.map((_, i) => i));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ?.id]);

  // Determine the index where HARD questions start (for challenge break)
  const firstHardIndex = useMemo(() => {
    return availableQuestions.findIndex((q) => q.difficulty === 'HARD');
  }, [availableQuestions]);

  // Timer effect
  useEffect(() => {
    if (gameOver || showResult || showChallengeBreak) return;
    if (timerFrozenRef.current) return;
    // Pause the countdown while an answer is locked in (and its server-side
    // validation is in flight) so a timeout can't double-penalize.
    if (selectedAnswer !== null) return;

    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      handleTimeUp();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, gameOver, showResult, showChallengeBreak, selectedAnswer]);

  // Reset timer when question changes
  useEffect(() => {
    if (currentQ) {
      setTimeLeft(TIMER_BY_DIFFICULTY[currentQ.difficulty]);
      answerStartTimeRef.current = Date.now();
      timerFrozenRef.current = false;
      setAnswerError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion]);

  // Show challenge break modal before first HARD question
  useEffect(() => {
    if (
      gameMode === 'STANDARD' &&
      firstHardIndex > 0 &&
      currentQuestion === firstHardIndex &&
      !challengeBreakShown &&
      !gameOver &&
      !showResult
    ) {
      setShowChallengeBreak(true);
      setChallengeBreakShown(true);
    }
  }, [currentQuestion, firstHardIndex, challengeBreakShown, gameOver, showResult, gameMode]);

  const handleTimeUp = () => {
    const newHearts = hearts - 1;
    setHearts(newHearts);
    setCorrectStreak(0);

    answerTimesRef.current.push(maxTime); // timed out = max time

    if (currentQ?.difficulty === 'HARD') hardTotalRef.current++;

    if (newHearts <= 0) {
      finishGame(true, newHearts);
    } else {
      advanceQuestion();
    }
  };

  const advanceQuestion = () => {
    if (currentQuestion < availableQuestions.length - 1) {
      setCurrentQuestion((q) => q + 1);
      setSelectedAnswer(null);
      setHiddenOptions(new Set());
    } else {
      finishGame(false, hearts);
    }
  };

  const finishGame = (isGameOver: boolean, finalHearts: number) => {
    // Mark questions as seen for dedup
    markSeen(availableQuestions.map((q) => q.id));

    if (isGameOver) {
      setGameOver(true);
    } else {
      setShowResult(true);
    }

    const avgTime =
      answerTimesRef.current.length > 0
        ? answerTimesRef.current.reduce((a, b) => a + b, 0) / answerTimesRef.current.length
        : 0;

    checkAfterGame({
      correctAnswers,
      totalQuestions: availableQuestions.length,
      questionsAnswered: currentQuestion + 1,
      totalPoints: score,
      maxStreak,
      hearts: finalHearts,
      avgAnswerTime: avgTime,
      hardQuestionsCorrect: hardCorrectRef.current,
      hardQuestionsTotal: hardTotalRef.current,
      gameMode,
      loginStreak: 0,
    });

    // Record the result to the leaderboard for authenticated players only.
    // Best-effort: the server derives identity from the SIWE session, so a
    // failure (offline, expired session) just leaves the player unranked this
    // round. A completed game counts all questions as answered; a game-over
    // stops at the current one.
    if (authedAddress) {
      const total = availableQuestions.length;
      const questionsAnswered = isGameOver ? currentQuestion + 1 : total;
      submitScore({
        correct: correctAnswersRef.current,
        questionsAnswered,
        total,
      }).catch(() => {
        /* unranked this round — no user-facing impact */
      });
    }
  };

  const handleAnswerSelect = async (answerIndex: number) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(answerIndex);

    const answerTimeMs = Date.now() - answerStartTimeRef.current;
    const answerTimeSeconds = answerTimeMs / 1000;

    const q = availableQuestions[currentQuestion];

    // Server-authoritative validation — the client never holds the answer key.
    // Run the reveal animation (500ms) and the network check concurrently.
    let result;
    try {
      const [res] = await Promise.all([
        checkAnswer(q.id, answerIndex),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
      result = res;
    } catch (err) {
      // Couldn't validate (e.g. backend unreachable). Let the player retry
      // without penalty rather than guessing at correctness.
      console.error('Answer validation failed:', err);
      setAnswerError('Could not verify your answer. Please check your connection and try again.');
      setSelectedAnswer(null);
      return;
    }
    setAnswerError(null);

    answerTimesRef.current.push(answerTimeSeconds);

    const correct = result.correct;
    const isHard = q.difficulty === 'HARD';

    if (isHard) hardTotalRef.current++;

    if (correct) {
      const newStreak = correctStreak + 1;
      setCorrectStreak(newStreak);
      if (newStreak > maxStreak) setMaxStreak(newStreak);
      correctAnswersRef.current += 1;
      setCorrectAnswers((c) => c + 1);

      // Calculate multiplied points
      const breakdown = calculateMultipliers(answerTimeSeconds, newStreak, userTier);
      setScore((s) => s + breakdown.total);
      setLastBreakdown(breakdown);
      popupKeyRef.current++;
      setShowPopup(true);
      setScoreAnimating(true);
      setTimeout(() => setScoreAnimating(false), 600);

      if (isHard) hardCorrectRef.current++;

      // Bonus heart every 3 correct in a row
      if (newStreak % 3 === 0 && hearts < 5) {
        setHearts((h) => h + 1);
      }

      // Achievement checks
      checkAfterAnswer({
        correctStreak: newStreak,
        maxStreak: Math.max(maxStreak, newStreak),
        score: score + breakdown.total,
        hearts,
        answerTimeSeconds,
        lastPointsEarned: breakdown.total,
        currentQuestionDifficulty: q.difficulty,
        wasCorrect: true,
      });
    } else {
      const newHearts = hearts - 1;
      setHearts(newHearts);
      setCorrectStreak(0);

      if (newHearts <= 0) {
        finishGame(true, newHearts);
        return;
      }
    }

    advanceQuestion();
  };

  const resetGame = () => {
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setHearts(3);
    setCorrectStreak(0);
    setMaxStreak(0);
    setCorrectAnswers(0);
    correctAnswersRef.current = 0;
    setTimeLeft(8);
    setGameOver(false);
    setShowChallengeBreak(false);
    setChallengeBreakShown(false);
    setHiddenOptions(new Set());
    setLastBreakdown(null);
    setShowPopup(false);
    setAnswerError(null);
    timerFrozenRef.current = false;
    answerTimesRef.current = [];
    hardCorrectRef.current = 0;
    hardTotalRef.current = 0;
    resetPowerUps();
  };

  // Power-up handlers
  const handleFiftyFifty = async () => {
    if (selectedAnswer !== null || !currentQ) return;
    if (powerUps.fiftyFifty <= 0) return;

    // The client doesn't know the answer — ask the server which wrong options
    // to hide. Only consume the power-up once the request succeeds.
    try {
      const { hide } = await fiftyFifty(currentQ.id);
      activateFiftyFifty();
      setHiddenOptions((prev) => new Set([...prev, ...hide]));
    } catch (err) {
      console.error('50/50 request failed:', err);
      setAnswerError('Could not use 50/50 right now. Please try again.');
    }
  };

  const handleTimeFreeze = () => {
    if (!activateTimeFreeze()) return;
    timerFrozenRef.current = true;
  };

  const handleSkip = () => {
    if (selectedAnswer !== null) return;
    if (!activateSkip()) return;
    // Skip: advance without score change or heart loss
    advanceQuestion();
  };

  const handleContinueToChallenge = () => {
    setShowChallengeBreak(false);
    if (currentQ) {
      setTimeLeft(TIMER_BY_DIFFICULTY[currentQ.difficulty]);
      answerStartTimeRef.current = Date.now();
    }
  };

  // Timer color
  const timerRatio = currentQ ? timeLeft / TIMER_BY_DIFFICULTY[currentQ.difficulty] : 1;
  // Warm ramp: calm gold with plenty of time → orange accent → warm red when critical.
  const timerColor = timerRatio > 0.6 ? '#d6a85f' : timerRatio > 0.3 ? '#f08a24' : '#e0533a';

  // Active multiplier for HUD badge
  const activeMultiplier = (() => {
    if (!currentQ) return 1;
    const elapsed = (Date.now() - answerStartTimeRef.current) / 1000;
    const breakdown = calculateMultipliers(elapsed, correctStreak, userTier);
    return parseFloat((breakdown.speedMultiplier * breakdown.streakMultiplier * breakdown.tierMultiplier).toFixed(1));
  })();

  // ----- RENDER -----

  if (showChallengeBreak) {
    return (
      <div className="challenge-break-modal">
        <div className="challenge-break-content">
          <h2>&#128640; CHALLENGE MODE INCOMING! &#128640;</h2>
          <div className="challenge-description">
            <p>You've made it to the hard questions!</p>
            <p>These will test not just your knowledge, but your adaptability.</p>
            <div className="challenge-warnings">
              <div className="challenge-item">&#9889; Answers may move around the screen</div>
              <div className="challenge-item">&#128260; Text might be mirrored or distorted</div>
              <div className="challenge-item">&#9200; Timer is shorter — only 4 seconds!</div>
              <div className="challenge-item">&#128151; Hearts still matter - stay focused!</div>
            </div>
            <p><strong>Ready to face the ultimate trivia challenge?</strong></p>
          </div>
          <button
            onClick={handleContinueToChallenge}
            className="continue-challenge-btn shimmer-animation"
          >
            BRING ON THE CHALLENGES!
          </button>
        </div>
      </div>
    );
  }

  if (gameOver || showResult) {
    return (
      <>
        <GameResult
          gameOver={gameOver}
          score={score}
          totalQuestions={availableQuestions.length}
          questionsAnswered={currentQuestion + (gameOver ? 1 : 0)}
          correctAnswers={correctAnswers}
          heartsRemaining={hearts}
          maxStreak={maxStreak}
          userTier={userTier}
          onPlayAgain={resetGame}
          onUpgrade={onUpgrade}
          sessionAchievements={sessionUnlocked}
        />
        <AchievementToast achievementId={toastQueue[0]} onDismiss={dismissToast} />
      </>
    );
  }

  if (availableQuestions.length === 0) {
    return (
      <div className="no-questions">
        <p>No questions available for your tier.</p>
        <button onClick={() => onUpgrade('PREMIUM')}>Upgrade to Premium</button>
      </div>
    );
  }

  const question = availableQuestions[currentQuestion];

  return (
    <div className="trivia-game">
      <GameHUD
        hearts={hearts}
        score={score}
        streak={correctStreak}
        activeMultiplier={activeMultiplier}
        currentQuestion={currentQuestion}
        totalQuestions={availableQuestions.length}
        scoreAnimating={scoreAnimating}
      />

      {question.difficulty !== 'EASY' && (
        <div className={`difficulty-indicator difficulty-${question.difficulty.toLowerCase()}`}>
          {question.difficulty === 'HARD' ? '&#128293; HARD QUESTION &#128293;' : '&#9888;&#65039; MEDIUM'}
        </div>
      )}

      <div className="timer-container">
        <div className="timer-circle">
          <div className="timer-text">{timeLeft}</div>
          <svg className="timer-svg" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" className="timer-background" />
            <circle
              cx="50"
              cy="50"
              r="45"
              className="timer-progress"
              style={{
                strokeDasharray: `${timerRatio * 283} 283`,
                stroke: timerColor,
                transform: 'rotate(-90deg)',
                transformOrigin: '50% 50%',
              }}
            />
          </svg>
        </div>
      </div>

      <div className="question">
        <h3>{question.question}</h3>
      </div>

      <div className={`options ${question.challengeType ? `challenge-${question.challengeType}` : ''}`}>
        {displayOrder.map((origIndex, displayPos) => (
          <button
            key={origIndex}
            className={`option ${selectedAnswer === origIndex ? 'selected' : ''} ${question.challengeType ? `option-${question.challengeType}` : ''}`}
            onClick={() => handleAnswerSelect(origIndex)}
            disabled={selectedAnswer !== null || hiddenOptions.has(origIndex)}
            style={{
              ...(question.challengeType === 'bounce' ? { animationDelay: `${displayPos * 0.5}s` } : {}),
              ...(hiddenOptions.has(origIndex) ? { visibility: 'hidden' as const } : {}),
            }}
          >
            <span className={question.challengeType === 'mirror' ? 'mirrored-text' : ''}>
              {question.options[origIndex]}
            </span>
          </button>
        ))}
      </div>

      {answerError && (
        <div className="answer-error" role="alert">
          {answerError}
        </div>
      )}

      <PowerUpBar
        remaining={powerUps}
        onFiftyFifty={handleFiftyFifty}
        onTimeFreeze={handleTimeFreeze}
        onSkip={handleSkip}
        disabled={selectedAnswer !== null}
      />

      {correctStreak > 0 && (
        <div className="streak-indicator">
          Correct Streak: {correctStreak}
          {correctStreak % 3 === 2 && ' (1 more for bonus heart!)'}
        </div>
      )}

      <MultiplierPopup key={popupKeyRef.current} breakdown={lastBreakdown} show={showPopup} />

      <AchievementToast achievementId={toastQueue[0]} onDismiss={dismissToast} />
    </div>
  );
};

export default TriviaGame;
